import { View, StyleSheet, Dimensions, Platform, ScrollView, useWindowDimensions, ActivityIndicator } from 'react-native';
import { Surface, Icon, Text, Button, useTheme, Card, Appbar } from 'react-native-paper';
import { useTranslation } from 'react-i18next';
import { useColorScheme } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
// Map removed from dashboard; GPS page owns maps
import { CompanyLicenses, getActiveUsersStatistics, ActiveUsersStatistics, getVehicleStatistics, VehicleStatistics, getInvitationStatistics, InvitationStatistics } from '../api/apiService';
import Logo from './Logo';
import { useState, useEffect, useRef } from 'react';
import { useCompanyContext } from '../context/CompanyContext';
import { SIDEBAR_WIDTH } from './Sidebar';
import type { OwnerBecomeDriverSeatState } from '../features/owner/ownerBecomeDriver';
// Use platform-conditional require to support web and native exports
// eslint-disable-next-line @typescript-eslint/no-var-requires
const Victory: any = Platform.OS === 'web' ? require('victory') : require('victory-native');

// Get screen dimensions dynamically
const { width: SCREEN_WIDTH, height: SCREEN_HEIGHT } = Dimensions.get('window');

// No map on dashboard


// Driver locations omitted on dashboard

interface OwnerDashboardContentProps {
  userData: {
    company: {
      name: string;
      nipc: string;
      numero_licenca: string;
    } | null;
  };
  licenseInfo: CompanyLicenses | null;
  companyStats: {
    currently_driving: number;
    avg_drive_time_hours: number;
    avg_distance_km: number;
  } | null;
  isLoading?: boolean;
  onOpenSidebar: () => void;
  onBuyLicense: () => void;
  onBuyGpsLicense: () => void;
  onInviteDriver: () => void;
  /**
   * D-046 (2026-10-11): capacity-aware owner become-driver presentation,
   * derived from the already-loaded company licence state. `available` shows
   * the become-driver card; `full` shows the sold-out card; `unknown` (loading
   * or failed licence read) shows neither, so capacity is never falsely
   * presented as sold out.
   */
  becomeDriverSeatState?: OwnerBecomeDriverSeatState;
  /** D-046: explicit become-driver submission (opens the form or reuses a saved profile). */
  onBecomeDriver?: () => void;
}

export const OwnerDashboardContent = ({
  userData,
  licenseInfo,
  companyStats,
  isLoading = false,
  onOpenSidebar,
  onBuyLicense,
  onBuyGpsLicense,
  onInviteDriver,
  becomeDriverSeatState = 'unknown',
  onBecomeDriver,
}: OwnerDashboardContentProps) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const colorScheme = useColorScheme();
  const userInterfaceStyle = colorScheme === 'dark' ? 'dark' : 'light';
  const { width, height } = useWindowDimensions();
  const insets = useSafeAreaInsets();
  const { selectedCompany } = useCompanyContext();
  const selectedCompanyId = selectedCompany?.id;
  const isLargeScreen = width > 1024;
  const displayCompany = selectedCompany ?? userData?.company ?? null;
  const displayLicense = displayCompany && 'numero_licenca' in displayCompany
    ? displayCompany.numero_licenca
    : '';
  const [trialModalVisible, setTrialModalVisible] = useState(false);
  const [activeUsersData, setActiveUsersData] = useState<ActiveUsersStatistics | null>(null);
  const [loadingActiveUsers, setLoadingActiveUsers] = useState(false);
  const [vehicleStatistics, setVehicleStatistics] = useState<VehicleStatistics | null>(null);
  const [loadingVehicleStatistics, setLoadingVehicleStatistics] = useState(false);
  const [invitationStatistics, setInvitationStatistics] = useState<InvitationStatistics | null>(null);
  const [loadingInvitationStatistics, setLoadingInvitationStatistics] = useState(false);
  const [statisticsCompanyId, setStatisticsCompanyId] = useState<number | undefined>(undefined);

  // Keep one request batch per selected company while still allowing a new batch
  // after a company switch. The request id logically cancels older batches so a
  // late response cannot repopulate statistics for a different company.
  const hasFetchedRef = useRef(false);
  const lastFetchedCompanyIdRef = useRef<number | undefined>(undefined);
  const statisticsRequestIdRef = useRef(0);
  const isMountedRef = useRef(false);
  const selectedCompanyIdRef = useRef(selectedCompanyId);
  selectedCompanyIdRef.current = selectedCompanyId;

  useEffect(() => {
    isMountedRef.current = true;

    // React StrictMode replays effects on mount. Reuse the first batch for the
    // same company, but allow every actual company change to start a new batch.
    if (hasFetchedRef.current && lastFetchedCompanyIdRef.current === selectedCompanyId) {
      return () => {
        isMountedRef.current = false;
      };
    }

    hasFetchedRef.current = true;
    lastFetchedCompanyIdRef.current = selectedCompanyId;
    const requestId = ++statisticsRequestIdRef.current;
    const isCurrentRequest = () =>
      isMountedRef.current
      && statisticsRequestIdRef.current === requestId
      && selectedCompanyIdRef.current === selectedCompanyId;

    // Clear the previous company's data before loading the new company's data.
    setActiveUsersData(null);
    setVehicleStatistics(null);
    setInvitationStatistics(null);
    setStatisticsCompanyId(undefined);
    setLoadingActiveUsers(true);
    setLoadingVehicleStatistics(true);
    setLoadingInvitationStatistics(true);

    // Do not make an unscoped request while the company context is settling.
    if (selectedCompanyId === undefined) {
      setLoadingActiveUsers(false);
      setLoadingVehicleStatistics(false);
      setLoadingInvitationStatistics(false);
      return () => {
        isMountedRef.current = false;
      };
    }

    const fetchAllStatistics = async () => {
      // Fetch all statistics in parallel for the explicitly selected company.
      const [activeUsersResponse, vehicleResponse, invitationResponse] = await Promise.all([
        getActiveUsersStatistics(selectedCompanyId),
        getVehicleStatistics(selectedCompanyId),
        getInvitationStatistics(selectedCompanyId),
      ]);

      if (!isCurrentRequest()) {
        return;
      }

      // Process active users data
      if (activeUsersResponse.ok && activeUsersResponse.data) {
        setActiveUsersData(activeUsersResponse.data);
      } else {
        setActiveUsersData({ dau: [], mau: [] });
      }
      setLoadingActiveUsers(false);

      // Process vehicle statistics
      if (vehicleResponse.ok && vehicleResponse.data) {
        setVehicleStatistics(vehicleResponse.data);
      } else {
        setVehicleStatistics({ oldest_vehicles: [], high_odometer: [] });
      }
      setLoadingVehicleStatistics(false);

      // Process invitation statistics
      if (invitationResponse.ok && invitationResponse.data) {
        setInvitationStatistics(invitationResponse.data);
      } else {
        setInvitationStatistics(null);
      }
      setStatisticsCompanyId(selectedCompanyId);
      setLoadingInvitationStatistics(false);
    };

    fetchAllStatistics().catch(error => {
      if (!isCurrentRequest()) {
        return;
      }
      console.error('Error fetching dashboard statistics:', error);
      // Set empty data on error
      setActiveUsersData({ dau: [], mau: [] });
      setVehicleStatistics({ oldest_vehicles: [], high_odometer: [] });
      setInvitationStatistics(null);
      setStatisticsCompanyId(selectedCompanyId);
      setLoadingActiveUsers(false);
      setLoadingVehicleStatistics(false);
      setLoadingInvitationStatistics(false);
    });

    return () => {
      isMountedRef.current = false;
    };
  }, [selectedCompanyId]);

  // Use real data only while it belongs to the currently selected company.
  // This render-time guard closes the stale-data window before the effect reset runs.
  const statisticsAreCurrent = selectedCompanyId !== undefined && statisticsCompanyId === selectedCompanyId;
  const dauData = statisticsAreCurrent ? activeUsersData?.dau ?? [] : [];
  const mauData = statisticsAreCurrent ? activeUsersData?.mau ?? [] : [];
  const mockedUnassignedByWeek = [5, 3, 7, 4, 6, 2];
  // Derive GPS utilization from available license info (real data)
  const gpsTotalLicenses = licenseInfo?.summary.gps_licenses ?? 0;
  const gpsAvailableLicenses = licenseInfo?.summary.available_gps_licenses ?? 0;
  const gpsUsedLicenses = Math.max(0, gpsTotalLicenses - gpsAvailableLicenses);
  const ownerCapacity = selectedCompany?.capacity ?? licenseInfo?.summary.capacity;
  const committedCompanyLicenses = ownerCapacity
    ? ownerCapacity.used + ownerCapacity.reserved
    : 0;
  const activeDrivers = ownerCapacity?.active_drivers ?? null;
  // Use real vehicle statistics if available (only high odometer, oldest vehicles removed)
  const highOdometerVehicles = statisticsAreCurrent ? vehicleStatistics?.high_odometer ?? [] : [];
  const displayedInvitationStatistics = statisticsAreCurrent ? invitationStatistics : null;

  // Simple helpers for stats
  const getAverage = (arr: number[]) => (arr.length ? arr.reduce((a, b) => a + b, 0) / arr.length : 0);
  const getLast = (arr: number[]) => (arr.length ? arr[arr.length - 1] : 0);
  const getPreviousPeriodAverage = (arr: number[]) => {
    if (arr.length < 2) return 0;
    const half = Math.floor(arr.length / 2);
    const previousHalf = arr.slice(0, half);
    return previousHalf.length ? previousHalf.reduce((a, b) => a + b, 0) / previousHalf.length : 0;
  };
  const getPercentageChange = (current: number, previous: number) => {
    if (previous === 0) return 0;
    return Math.round(((current - previous) / previous) * 100);
  };

  // Utility to map arrays to Victory data points
  const toSeries = (arr: number[]) => arr.map((y, x) => ({ x: x + 1, y }));

  // Pad data to 28 days (fill with 0s at the beginning if data is shorter)
  const padDataTo28Days = (data: number[]): number[] => {
    if (data.length >= 28) {
      return data.slice(-28); // Take last 28 if more
    }
    const padding = new Array(28 - data.length).fill(0);
    return [...padding, ...data];
  };

  // Generate date labels for the last 28 days - only show first and last date
  const generateDateLabels = () => {
    const labels: string[] = [];
    const today = new Date();
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    
    for (let i = 27; i >= 0; i--) {
      const date = new Date(today);
      date.setDate(date.getDate() - i);
      // Only show first and last date
      if (i === 27 || i === 0) {
        labels.push(`${monthNames[date.getMonth()]} ${date.getDate()}`);
      } else {
        labels.push('');
      }
    }
    return labels;
  };

  // Auto-show trial modal for owners if company never had any license
  useEffect(() => {
    if (licenseInfo?.summary.ever_had_licenses === false) {
      setTrialModalVisible(true);
    }
  }, [licenseInfo]);

  // Compute trial days left if user has a trialing subscription
  const trialSub = licenseInfo?.subscriptions?.find(sub => sub.status === 'trialing');
  const trialDaysLeft = trialSub
    ? Math.max(
        Math.ceil(
          (new Date(trialSub.current_period_end).getTime() - Date.now()) /
            (1000 * 60 * 60 * 24)
        ),
        0
      )
    : null;

  // Calcular height total do header incluindo status bar
  const totalHeaderHeight = (() => {
    if (isLargeScreen) return 0;
    const headerHeight = 48; // Altura compacta para mobile
    const statusBarPadding = Platform.OS === 'ios' ? insets.top : Platform.OS === 'android' ? insets.top : 0;
    return headerHeight + statusBarPadding;
  })();

  const renderHeader = () => {
    if (isLargeScreen) return null;
    
    // Usar altura fixa ao invés de porcentagem para evitar header muito grande
    const headerHeight = 48; // Altura compacta para mobile
    // Adicionar padding da status bar para evitar sobreposição
    const topPadding = Platform.OS === 'ios' ? insets.top : Platform.OS === 'android' ? insets.top : 0;
    
    return (
      <Appbar.Header style={{ 
        height: headerHeight + topPadding, // Incluir padding no height total
        backgroundColor: theme.colors.surface,
        position: 'absolute',
        top: 0, // Começar do topo
        left: 0,
        right: 0,
        zIndex: 1,
        alignItems: 'center',
        paddingTop: topPadding, // Adicionar padding interno
      }}>
        <Appbar.Action
          icon="menu"
          onPress={onOpenSidebar}
          color={theme.colors.onSurface}
          testID="owner-sidebar-menu-trigger"
        />
        <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center' }}>
          <Logo size={Platform.OS === 'web' ? 'medium' : 'mobile'} />
        </View>
        <View style={{ width: 48 }} />
      </Appbar.Header>
    );
  };

  return (
    <View style={{ flex: 1, backgroundColor: theme.colors.background }}>
      {renderHeader()}
      
      {isLoading ? (
        <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center' }}>
          <ActivityIndicator size="large" />
          <Text style={{ marginTop: 16, color: theme.colors.onSurface }}>
            {t('ownerDashboard.loadingLicenses', 'Loading license information...')}
          </Text>
        </View>
      ) : (
        <ScrollView 
          style={[
            styles.contentContainer, 
            !isLargeScreen && { marginTop: totalHeaderHeight }
          ]}
        >
        <Card style={styles.card}>
          <Card.Content>
            <View style={[
              styles.companyHeader,
              !isLargeScreen && styles.companyHeaderMobile
            ]}>
              <View style={styles.companyInfo}>
                <Text variant="headlineMedium" style={{ color: theme.colors.primary, marginBottom: 8 }}>
                  {displayCompany?.name || ''}
                </Text>
                <View style={styles.companyDetails}>
                  <Text variant="bodyMedium" style={{ color: theme.colors.onSurfaceVariant }}>
                    NIPC: {displayCompany?.nipc || ''}
                  </Text>
                  <Text variant="bodyMedium" style={{ color: theme.colors.onSurfaceVariant }}>
                    Licença: {displayLicense}
                  </Text>
                  {trialDaysLeft !== null && (
                    <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }}>
                      <Icon source="timer" size={18} color={theme.colors.primary} />
                      <Text variant="bodyMedium" style={{ color: theme.colors.primary }}>
                        {t('ownerDashboard.trialDaysLeft', 'Trial Days Left')}: {trialDaysLeft} {t('ownerDashboard.days', 'days')}
                      </Text>
                    </View>
                  )}
                </View>
              </View>
              {/* Removed top-line numbers to avoid duplication with KPI cards below */}
            </View>
          </Card.Content>
        </Card>


        {/* Action Buttons */}
        <View style={[
          styles.actionButtonsContainer,
          isLargeScreen && styles.actionButtonsContainerDesktop
        ]}>
          {/* D-046 (2026-10-11): with a company seat free, the owner is invited
              to also drive. A single CTA submits the become-driver action
              (new profile or reuse of a saved one); no purchase prompt here. */}
          {becomeDriverSeatState === 'available' && onBecomeDriver && (
            <Card
              testID="owner-become-driver-card"
              style={[
                styles.actionCard,
                isLargeScreen && styles.actionCardDesktop
              ]}
            >
              <Card.Content style={styles.actionCardContent}>
                <View style={[styles.actionRow, { flexDirection: 'column', alignItems: 'flex-start', gap: 12 }]}>
                  <View style={styles.actionInfo}>
                    <Icon source="steering" size={24} color={theme.colors.primary} />
                    <View style={styles.actionTextContainer}>
                      <Text variant="titleMedium" style={{ color: theme.colors.onSurface }}>
                        {t('ownerDashboard.becomeDriverTitle', 'Quer também conduzir?')}
                      </Text>
                    </View>
                  </View>
                  <Button
                    testID="owner-complete-driver-cta"
                    mode="contained-tonal"
                    onPress={onBecomeDriver}
                    style={[styles.actionButton, { marginLeft: 0, marginTop: 12, alignSelf: 'flex-end' }]}
                    labelStyle={styles.actionButtonLabel}
                  >
                    {t('ownerDashboard.becomeDriverCta', 'Tornar-me motorista')}
                  </Button>
                </View>
              </Card.Content>
            </Card>
          )}

          {/* D-046 (2026-10-11): every company seat is taken. The only action
              is the explicit purchase CTA; no profile form is offered until
              capacity exists, and no redundant activation control remains. */}
          {becomeDriverSeatState === 'full' && (
            <Card
              testID="owner-driver-license-required-card"
              style={[
                styles.actionCard,
                isLargeScreen && styles.actionCardDesktop
              ]}
            >
              <Card.Content style={styles.actionCardContent}>
                <View style={[styles.actionRow, { flexDirection: 'column', alignItems: 'flex-start', gap: 12 }]}>
                  <View style={styles.actionInfo}>
                    <Icon source="alert-circle-outline" size={24} color={theme.colors.error} />
                    <View style={styles.actionTextContainer}>
                      <Text variant="titleMedium" style={{ color: theme.colors.onSurface }}>
                        {t('ownerDashboard.seatsInUseTitle', 'Todas as licenças estão em uso')}
                      </Text>
                    </View>
                  </View>
                  <Button
                    testID="owner-buy-one-more-cta"
                    mode="contained-tonal"
                    onPress={onBuyLicense}
                    style={[styles.actionButton, { marginLeft: 0, marginTop: 12, alignSelf: 'flex-end' }]}
                    labelStyle={styles.actionButtonLabel}
                  >
                    {t('ownerDashboard.buyLicenseCta', 'Comprar licença')}
                  </Button>
                </View>
              </Card.Content>
            </Card>
          )}

          <Card style={[
            styles.actionCard,
            isLargeScreen && styles.actionCardDesktop
          ]}>
            <Card.Content style={styles.actionCardContent}>
              <View style={[styles.actionRow, { flexDirection: 'column', alignItems: 'flex-start', gap: 12 }]}>
                <View style={styles.actionInfo}>
                  <Icon source="account-plus" size={24} color={theme.colors.primary} />
                  <View style={styles.actionTextContainer}>
                    <Text variant="titleMedium" style={{ color: theme.colors.onSurface }}>
                      {t('ownerDashboard.inviteDriver', 'Invite Driver')}
                    </Text>
                    <Text variant="bodySmall" style={{ color: theme.colors.onSurfaceVariant }}>
                      {t('ownerDashboard.inviteDriverDesc', 'Send invitation to new drivers')}
                    </Text>
                  </View>
                </View>
                <Button
                  testID="owner-invite-driver-cta"
                  mode="contained-tonal"
                  onPress={onInviteDriver}
                  style={[styles.actionButton, { marginLeft: 0, marginTop: 12, alignSelf: 'flex-end' }]}
                  labelStyle={styles.actionButtonLabel}
                >
                  {t('common.invite', 'Invite')}
                </Button>
              </View>
            </Card.Content>
          </Card>

          {/* Hours Licenses */}
          {/* Unified Buy Licenses CTA */}
          <Card style={[
            styles.actionCard,
            isLargeScreen && styles.actionCardDesktop
          ]}>
            <Card.Content style={styles.actionCardContent}>
              <View style={[styles.actionRow, { flexDirection: 'column', alignItems: 'flex-start', gap: 12 }]}>
                <View style={styles.actionInfo}>
                  <Icon source="credit-card" size={24} color={theme.colors.primary} />
                  <View style={styles.actionTextContainer}>
                    <Text variant="titleMedium" style={{ color: theme.colors.onSurface }}>
                      {t('ownerDashboard.buyLicenses', 'Buy Licenses')}
                    </Text>
                    <Text variant="bodySmall" style={{ color: theme.colors.onSurfaceVariant }}>
                      {t('ownerDashboard.buyLicensesDesc', 'Purchase additional driver licenses')}
                    </Text>
                  </View>
                </View>
                <Button
                  mode="contained-tonal"
                  onPress={onBuyLicense}
                  style={[styles.actionButton, { marginLeft: 0, marginTop: 12, alignSelf: 'flex-end' }]}
                  labelStyle={styles.actionButtonLabel}
                >
                  {t('common.buy', 'Buy')}
                </Button>
              </View>
            </Card.Content>
          </Card>
        </View>

        {/* Statistics Section */}
        <View style={styles.statsContainer}>
          <Text variant="titleLarge" style={{ marginBottom: 16, color: theme.colors.primary }}>
            {t('ownerDashboard.companyStats', 'Company Statistics')}
          </Text>
          
          {/* Row 1: DAU, MAU */}
          <View style={[
            styles.metricsGrid,
            !isLargeScreen && styles.metricsGridMobile
          ]}>
            {/* DAU */}
            <Card style={[
              styles.metricCard,
              !isLargeScreen && styles.metricCardMobile
            ]}>
              <Card.Content style={styles.metricCardContent}>
                <View style={styles.metricHeaderWithMenu}>
                  <View style={styles.metricHeader}>
                    <Icon source="account-clock" size={24} color={theme.colors.primary} />
                    <Text variant="titleMedium" style={{ color: theme.colors.onSurfaceVariant, marginLeft: 8 }}>
                      {t('ownerDashboard.dailyActiveUsers', 'Daily active users')}
                    </Text>
                  </View>
                  <Icon source="dots-vertical" size={20} color={theme.colors.onSurfaceVariant} />
                </View>
                {(() => {
                  const paddedDauData = padDataTo28Days(dauData);
                  const avg = getAverage(paddedDauData);
                  const previousAvg = getPreviousPeriodAverage(paddedDauData);
                  const percentageChange = getPercentageChange(avg, previousAvg);
                  const dateLabels = generateDateLabels();
                  const previousPeriodData = paddedDauData.length > 0 ? paddedDauData.slice(0, Math.floor(paddedDauData.length / 2)) : [];
                  const previousSeries = previousPeriodData.length > 0 
                    ? [...Array(paddedDauData.length - previousPeriodData.length).fill(null), ...previousPeriodData.map((y, x) => ({ x: x + 1, y }))]
                    : [];
                  
                  return (
                    <View style={{ marginTop: 4 }}>
                      <View style={styles.metricValueContainer}>
                        <Text variant="headlineSmall" style={{ color: theme.colors.onSurface, fontWeight: '600', fontSize: 14 }}>
                          {avg.toFixed(1)} {t('ownerDashboard.average', 'average')}
                        </Text>
                        {percentageChange !== 0 && (
                          <View style={styles.percentageChangeContainer}>
                            <Icon 
                              source={percentageChange > 0 ? "arrow-up" : "arrow-down"} 
                              size={10} 
                              color={percentageChange > 0 ? "#10B981" : "#EF4444"} 
                            />
                            <Text style={{ 
                              color: percentageChange > 0 ? "#10B981" : "#EF4444", 
                              fontSize: 10, 
                              fontWeight: '600' 
                            }}>
                              {Math.abs(percentageChange)}%
                            </Text>
                          </View>
                        )}
                      </View>
                      {paddedDauData.length > 0 ? (
                        <>
                          {Platform.OS === 'web' && Victory?.VictoryChart ? (
                            <Victory.VictoryChart
                              theme={Victory.VictoryTheme ? Victory.VictoryTheme.material : undefined}
                              height={100}
                              padding={{ top: 6, bottom: 18, left: 20, right: 10 }}
                              domain={{ x: [1, 28] }}
                              containerComponent={
                                Victory.VictoryVoronoiContainer && Victory.VictoryTooltip
                                  ? (
                                    <Victory.VictoryVoronoiContainer
                                      voronoiDimension="x"
                                      labels={({ datum }: any) => `${datum.y}`}
                                      labelComponent={<Victory.VictoryTooltip style={{ fontSize: 7 }} constrainToVisibleArea />}
                                    />
                                  )
                                  : undefined
                              }
                            >
                              <Victory.VictoryAxis 
                                dependentAxis 
                                style={{ 
                                  tickLabels: { fontSize: 5, fill: theme.colors.onSurfaceVariant },
                                  axis: { stroke: theme.colors.surfaceVariant },
                                  grid: { stroke: theme.colors.surfaceVariant, strokeWidth: 0.5 }
                                }} 
                              />
                              <Victory.VictoryAxis 
                                domain={[1, 28]}
                                tickValues={[1, 28]}
                                style={{ 
                                  tickLabels: { fontSize: 5, fill: theme.colors.onSurfaceVariant },
                                  axis: { stroke: theme.colors.surfaceVariant }
                                }}
                                tickFormat={(tick: number) => {
                                  const index = tick - 1;
                                  return dateLabels[index] || '';
                                }}
                              />
                              {previousSeries.length > 0 && previousSeries.some((d: any) => d !== null && d.y !== null) && (
                                <Victory.VictoryLine 
                                  data={previousSeries.filter((d: any) => d !== null && d.y !== null)} 
                                  style={{ 
                                    data: { 
                                      stroke: theme.colors.primary, 
                                      strokeWidth: 1.5,
                                      strokeDasharray: '3,3',
                                      opacity: 0.6
                                    } 
                                  }} 
                                  interpolation="monotoneX" 
                                />
                              )}
                              <Victory.VictoryLine 
                                data={toSeries(paddedDauData)} 
                                style={{ 
                                  data: { 
                                    stroke: theme.colors.primary, 
                                    strokeWidth: 2 
                                  } 
                                }} 
                                interpolation="monotoneX" 
                              />
                            </Victory.VictoryChart>
                          ) : (
                            <View style={{ alignItems: 'center', justifyContent: 'center', height: 100 }}>
                              <Text style={{ color: theme.colors.onSurfaceVariant, fontSize: 12 }}>{t('ownerDashboard.chartOnWeb', 'Chart on web')}</Text>
                            </View>
                          )}
                        </>
                      ) : (
                        <View style={{ alignItems: 'center', justifyContent: 'center', height: 100 }}>
                          <Text style={{ color: theme.colors.onSurfaceVariant }}>{t('ownerDashboard.noData', 'No data available')}</Text>
                        </View>
                      )}
                    </View>
                  );
                })()}
              </Card.Content>
            </Card>

            {/* MAU */}
            <Card style={[
              styles.metricCard,
              !isLargeScreen && styles.metricCardMobile
            ]}>
              <Card.Content style={styles.metricCardContent}>
                <View style={styles.metricHeaderWithMenu}>
                  <View style={styles.metricHeader}>
                    <Icon source="calendar-account" size={24} color={theme.colors.primary} />
                    <Text variant="titleMedium" style={{ color: theme.colors.onSurfaceVariant, marginLeft: 8 }}>
                      {t('ownerDashboard.monthlyActiveUsers', 'Monthly active users')}
                    </Text>
                  </View>
                  <Icon source="dots-vertical" size={20} color={theme.colors.onSurfaceVariant} />
                </View>
                {(() => {
                  const paddedMauData = padDataTo28Days(mauData);
                  const avg = getAverage(paddedMauData);
                  const previousAvg = getPreviousPeriodAverage(paddedMauData);
                  const percentageChange = getPercentageChange(avg, previousAvg);
                  const dateLabels = generateDateLabels();
                  const previousPeriodData = paddedMauData.length > 0 ? paddedMauData.slice(0, Math.floor(paddedMauData.length / 2)) : [];
                  const previousSeries = previousPeriodData.length > 0 
                    ? [...Array(paddedMauData.length - previousPeriodData.length).fill(null), ...previousPeriodData.map((y, x) => ({ x: x + 1, y }))]
                    : [];
                  
                  return (
                    <View style={{ marginTop: 4 }}>
                      <View style={styles.metricValueContainer}>
                        <Text variant="headlineSmall" style={{ color: theme.colors.onSurface, fontWeight: '600', fontSize: 14 }}>
                          {Math.round(avg)} {t('ownerDashboard.average', 'average')}
                        </Text>
                        {percentageChange !== 0 && (
                          <View style={styles.percentageChangeContainer}>
                            <Icon 
                              source={percentageChange > 0 ? "arrow-up" : "arrow-down"} 
                              size={10} 
                              color={percentageChange > 0 ? "#10B981" : "#EF4444"} 
                            />
                            <Text style={{ 
                              color: percentageChange > 0 ? "#10B981" : "#EF4444", 
                              fontSize: 10, 
                              fontWeight: '600' 
                            }}>
                              {Math.abs(percentageChange)}%
                            </Text>
                          </View>
                        )}
                      </View>
                      {paddedMauData.length > 0 ? (
                        <>
                          {Platform.OS === 'web' && Victory?.VictoryChart ? (
                            <Victory.VictoryChart
                              theme={Victory.VictoryTheme ? Victory.VictoryTheme.material : undefined}
                              height={100}
                              padding={{ top: 6, bottom: 18, left: 20, right: 10 }}
                              domain={{ x: [1, 28] }}
                              containerComponent={
                                Victory.VictoryVoronoiContainer && Victory.VictoryTooltip
                                  ? (
                                    <Victory.VictoryVoronoiContainer
                                      voronoiDimension="x"
                                      labels={({ datum }: any) => `${datum.y}`}
                                      labelComponent={<Victory.VictoryTooltip style={{ fontSize: 7 }} constrainToVisibleArea />}
                                    />
                                  )
                                  : undefined
                              }
                            >
                              <Victory.VictoryAxis 
                                dependentAxis 
                                style={{ 
                                  tickLabels: { fontSize: 5, fill: theme.colors.onSurfaceVariant },
                                  axis: { stroke: theme.colors.surfaceVariant },
                                  grid: { stroke: theme.colors.surfaceVariant, strokeWidth: 0.5 }
                                }} 
                              />
                              <Victory.VictoryAxis 
                                domain={[1, 28]}
                                tickValues={[1, 28]}
                                style={{ 
                                  tickLabels: { fontSize: 5, fill: theme.colors.onSurfaceVariant },
                                  axis: { stroke: theme.colors.surfaceVariant }
                                }}
                                tickFormat={(tick: number) => {
                                  const index = tick - 1;
                                  return dateLabels[index] || '';
                                }}
                              />
                              {previousSeries.length > 0 && previousSeries.some((d: any) => d !== null && d.y !== null) && (
                                <Victory.VictoryLine 
                                  data={previousSeries.filter((d: any) => d !== null && d.y !== null)} 
                                  style={{ 
                                    data: { 
                                      stroke: theme.colors.primary, 
                                      strokeWidth: 1.5,
                                      strokeDasharray: '3,3',
                                      opacity: 0.6
                                    } 
                                  }} 
                                  interpolation="monotoneX" 
                                />
                              )}
                              <Victory.VictoryLine 
                                data={toSeries(paddedMauData)} 
                                style={{ 
                                  data: { 
                                    stroke: theme.colors.primary, 
                                    strokeWidth: 2 
                                  } 
                                }} 
                                interpolation="monotoneX" 
                              />
                            </Victory.VictoryChart>
                          ) : (
                            <View style={{ alignItems: 'center', justifyContent: 'center', height: 100 }}>
                              <Text style={{ color: theme.colors.onSurfaceVariant, fontSize: 12 }}>{t('ownerDashboard.chartOnWeb', 'Chart on web')}</Text>
                            </View>
                          )}
                        </>
                      ) : (
                        <View style={{ alignItems: 'center', justifyContent: 'center', height: 100 }}>
                          <Text style={{ color: theme.colors.onSurfaceVariant }}>{t('ownerDashboard.noData', 'No data available')}</Text>
                        </View>
                      )}
                    </View>
                  );
                })()}
              </Card.Content>
            </Card>

          </View>

          {/* Row 2: authoritative company capacity and GPS usage */}
          <View
            testID="owner-company-capacity-grid"
            style={[
              styles.metricsGrid,
              !isLargeScreen && styles.metricsGridMobile
            ]}
          >
            <Card testID="owner-license-capacity-card" style={[styles.metricCard, styles.metricCardHighlighted, !isLargeScreen && styles.metricCardMobile]}>
              <Card.Content style={styles.metricCardContent}>
                <View style={styles.metricHeader}>
                  <Icon source="account-group" size={24} color={theme.colors.primary} />
                  <Text variant="titleMedium" style={{ color: theme.colors.onSurfaceVariant, marginLeft: 8 }}>
                    {t('ownerDashboard.licensesAndDrivers', 'Licenses and drivers')}
                  </Text>
                </View>
                <View testID="owner-funding-capacity" style={styles.capacitySummary}>
                  {ownerCapacity ? (
                    <>
                      <View style={styles.capacityMetricRow}>
                        <Text testID="owner-capacity-bullet-company-licenses" variant="bodyLarge" style={{ color: theme.colors.primary }}>•</Text>
                        <Text variant="bodyLarge" style={{ color: theme.colors.onSurface }}>
                          <Text testID="owner-capacity-label-company-licenses" style={styles.capacityMetricLabel}>
                            {t('ownerDashboard.companyLicensesLabel', 'Company licenses')}: {' '}
                          </Text>
                          <Text testID="owner-capacity-value-company-licenses">
                            {`${committedCompanyLicenses} ${t('ownerDashboard.ofLabel', 'of')} ${ownerCapacity.purchased} ${t('ownerDashboard.usedLabel', 'used')}`}
                          </Text>
                        </Text>
                      </View>
                      {activeDrivers ? (
                        <View style={styles.capacityMetricRow}>
                          <Text testID="owner-capacity-bullet-active-drivers" variant="bodyLarge" style={{ color: theme.colors.primary }}>•</Text>
                          <View style={styles.capacityMetricContent}>
                            <Text variant="bodyLarge" style={{ color: theme.colors.onSurface }}>
                              <Text testID="owner-capacity-label-active-drivers" style={styles.capacityMetricLabel}>
                                {t('ownerDashboard.activeDriversLabel', 'Active drivers')}: {' '}
                              </Text>
                              <Text testID="owner-capacity-value-active-drivers">
                                {`${activeDrivers.total} ${t('ownerDashboard.totalLabel', 'total')}`}
                              </Text>
                            </Text>
                            <Text testID="owner-capacity-value-active-drivers-breakdown" variant="bodyMedium" style={{ color: theme.colors.onSurfaceVariant }}>
                              {`${activeDrivers.self} self-pay · ${activeDrivers.company} ${t('ownerDashboard.companyLabel', 'company')}`}
                            </Text>
                          </View>
                        </View>
                      ) : (
                        <View style={styles.capacityMetricRow}>
                          <Text testID="owner-capacity-bullet-active-drivers" variant="bodyLarge" style={{ color: theme.colors.primary }}>•</Text>
                          <Text variant="bodyLarge" style={{ color: theme.colors.onSurfaceVariant }}>
                            <Text testID="owner-capacity-label-active-drivers" style={styles.capacityMetricLabel}>
                              {t('ownerDashboard.activeDriversLabel', 'Active drivers')}: {' '}
                            </Text>
                            <Text testID="owner-capacity-value-active-drivers">
                              {t('ownerDashboard.activeDriversUnavailable', 'unavailable')}
                            </Text>
                          </Text>
                        </View>
                      )}
                      <View style={styles.capacityMetricRow}>
                        <Text testID="owner-capacity-bullet-reserved-by-invitation" variant="bodyLarge" style={{ color: theme.colors.primary }}>•</Text>
                        <Text variant="bodyLarge" style={{ color: theme.colors.onSurface }}>
                          <Text testID="owner-capacity-label-reserved-by-invitation" style={styles.capacityMetricLabel}>
                            {t('ownerDashboard.reservedByInvitationLabel', 'Reserved by invitation')}: {' '}
                          </Text>
                          <Text testID="owner-capacity-value-reserved-by-invitation">{ownerCapacity.reserved}</Text>
                        </Text>
                      </View>
                      {activeDrivers && activeDrivers.ambiguous > 0 && (
                        <Text variant="bodyMedium" style={{ color: theme.colors.error }}>
                          {`${t('ownerDashboard.payerToConfirmLabel', 'Payer to confirm')}: ${activeDrivers.ambiguous}`}
                        </Text>
                      )}
                    </>
                  ) : (
                    <Text style={{ color: theme.colors.onSurfaceVariant }}>
                      {t('ownerDashboard.capacityUnavailable', 'Capacity unavailable')}
                    </Text>
                  )}
                </View>
              </Card.Content>
            </Card>

            {/* GPS: Active devices vs licenses */}
            <Card style={[styles.metricCard, !isLargeScreen && styles.metricCardMobile]}>
              <Card.Content style={styles.metricCardContent}>
                <View style={styles.metricHeader}>
                  <Icon source="satellite-variant" size={24} color={theme.colors.primary} />
                  <Text variant="titleMedium" style={{ color: theme.colors.onSurfaceVariant, marginLeft: 8 }}>
                    {t('ownerDashboard.gpsActiveDevices', 'GPS Active Devices')}
                  </Text>
                </View>
                <Text variant="headlineMedium" style={{ color: theme.colors.primary, marginVertical: 8, textAlign: 'center' }}>
                  {gpsUsedLicenses}/{gpsTotalLicenses}
                </Text>
                <View style={{ height: 10, backgroundColor: theme.colors.surfaceVariant, borderRadius: 6, overflow: 'hidden' }}>
                  <View style={{ width: `${Math.min(100, (gpsTotalLicenses > 0 ? (gpsUsedLicenses / gpsTotalLicenses) * 100 : 0))}%`, height: '100%', backgroundColor: theme.colors.primary }} />
                </View>
              </Card.Content>
            </Card>

            {/* Removed: Unassigned active drivers card per customer preference */}
          </View>

          {/* Row 3: Secondary cards */}
          <View
            testID="owner-invitation-statistics-grid"
            style={[
              styles.metricsGrid,
              !isLargeScreen && styles.metricsGridMobile
            ]}
          >
            <Card style={[styles.metricCard, !isLargeScreen && styles.metricCardMobile]}>
              <Card.Content style={styles.metricCardContent}>
                <View style={styles.metricHeader}>
                  <Icon source="counter" size={24} color={theme.colors.primary} />
                  <Text variant="titleMedium" style={{ color: theme.colors.onSurfaceVariant, marginLeft: 8 }}>
                    {t('ownerDashboard.highOdometer', 'Top High Odometer')}
                  </Text>
                </View>
                {highOdometerVehicles.length > 0 ? (
                  <View style={{ gap: 8, marginTop: 6 }}>
                    {highOdometerVehicles.map((v, i) => (
                      <View key={i} style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                        <Text style={{ width: 130, color: theme.colors.onSurface }}>{v.name}</Text>
                        <View style={{ flex: 1, height: 8, backgroundColor: theme.colors.surfaceVariant, borderRadius: 6, overflow: 'hidden' }}>
                          <View style={{ width: `${Math.min(100, (v.km / 200000) * 100)}%`, height: '100%', backgroundColor: theme.colors.primary }} />
                        </View>
                        <Text style={{ width: 80, textAlign: 'right', color: theme.colors.onSurfaceVariant }}>{new Intl.NumberFormat('pt-PT').format(v.km)} km</Text>
                      </View>
                    ))}
                  </View>
                ) : (
                  <View style={{ marginTop: 6, alignItems: 'center', justifyContent: 'center', minHeight: 60 }}>
                    <Text style={{ color: theme.colors.onSurfaceVariant }}>{t('ownerDashboard.noData', 'No data available')}</Text>
                  </View>
                )}
              </Card.Content>
            </Card>

            <Card testID="owner-invitation-statistics-card" style={[styles.metricCard, !isLargeScreen && styles.metricCardMobile]}>
              <Card.Content style={styles.metricCardContent}>
                <View style={styles.metricHeader}>
                  <Icon source="email-fast" size={24} color={theme.colors.primary} />
                  <Text variant="titleLarge" style={{ color: theme.colors.onSurfaceVariant, marginLeft: 8, flexShrink: 1 }}>
                    {t('ownerDashboard.invitations', 'Invitations (7/30 days)')}
                  </Text>
                </View>
                <View style={{ marginTop: 8 }}>
                  <Text variant="bodySmall" style={{ color: theme.colors.onSurfaceVariant, marginBottom: 8 }}>
                    {t('ownerDashboard.invitationSubsetNote', 'Invitations are grouped by creation window; the 7-day group is included in the 30-day group.')}
                  </Text>
                  {loadingInvitationStatistics ? (
                    <ActivityIndicator />
                  ) : displayedInvitationStatistics ? (
                    <View style={{ flexDirection: 'row', gap: 16 }}>
                    <View style={{ flex: 1, gap: 4 }}>
                      <Text variant="titleSmall" style={{ color: theme.colors.onSurface, fontWeight: '700' }}>
                        {t('ownerDashboard.invitation7Days', '7 days')}
                      </Text>
                      <Text variant="bodySmall">{t('ownerDashboard.invitationSent', 'Sent')}: {displayedInvitationStatistics?.last7?.sent ?? 0}</Text>
                      <Text variant="bodySmall">{t('ownerDashboard.invitationAccepted', 'Accepted')}: {displayedInvitationStatistics?.last7?.accepted ?? 0}</Text>
                      <Text variant="bodySmall">{t('ownerDashboard.invitationExpired', 'Expired')}: {displayedInvitationStatistics?.last7?.expired ?? 0}</Text>
                    </View>
                    <View style={{ flex: 1, gap: 4 }}>
                      <Text variant="titleSmall" style={{ color: theme.colors.onSurface, fontWeight: '700' }}>
                        {t('ownerDashboard.invitation30Days', '30 days')}
                      </Text>
                      <Text variant="bodySmall">{t('ownerDashboard.invitationSent', 'Sent')}: {displayedInvitationStatistics?.last30?.sent ?? 0}</Text>
                      <Text variant="bodySmall">{t('ownerDashboard.invitationAccepted', 'Accepted')}: {displayedInvitationStatistics?.last30?.accepted ?? 0}</Text>
                      <Text variant="bodySmall">{t('ownerDashboard.invitationExpired', 'Expired')}: {displayedInvitationStatistics?.last30?.expired ?? 0}</Text>
                    </View>
                    </View>
                  ) : (
                    <Text style={{ color: theme.colors.onSurfaceVariant }}>
                      {t('ownerDashboard.invitationUnavailable', 'Invitation data unavailable')}
                    </Text>
                  )}
                </View>
              </Card.Content>
            </Card>

            {/* GPS: Devices offline >24h */}
            <Card style={[styles.metricCard, !isLargeScreen && styles.metricCardMobile]}>
              <Card.Content style={styles.metricCardContent}>
                <View style={styles.metricHeader}>
                  <Icon source="access-point-network-off" size={24} color={theme.colors.error || '#B00020'} />
                  <Text variant="titleMedium" style={{ color: theme.colors.onSurfaceVariant, marginLeft: 8 }}>
                    {t('ownerDashboard.gpsDevicesOffline', 'Devices Offline >24h')}
                  </Text>
                </View>
                <Text variant="headlineMedium" style={{ color: theme.colors.error || '#B00020', marginVertical: 8, textAlign: 'center' }}>
                  —
                </Text>
              </Card.Content>
            </Card>

            
          </View>
        </View>
      </ScrollView>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  contentContainer: {
    flex: 1,
    padding: 20,
  },
  contentWithSidebar: {
    marginLeft: 240, // Width of the sidebar
  },
  card: {
    marginBottom: 20,
    elevation: 4,
  },
  companyHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: 24,
  },
  companyHeaderMobile: {
    flexDirection: 'column',
    gap: 16,
  },
  companyInfo: {
    flex: 1,
  },
  companyDetails: {
    gap: 4,
  },
  metricsContainer: {
    flexDirection: 'row',
    borderRadius: 12,
    padding: 16,
    gap: 16,
  },
  metricsContainerMobile: {
    flexDirection: 'column',
    width: '100%',
    gap: 12,
  },
  metricItem: {
    alignItems: 'center',
    minWidth: 100,
  },
  metricDivider: {
    width: 1,
    height: '100%',
  },
  actionButtonsContainer: {
    gap: 12,
    marginTop: 24,
  },
  actionButtonsContainerDesktop: {
    flexDirection: 'row',
    gap: 16,
  },
  actionCard: {
    elevation: 1,
  },
  actionCardDesktop: {
    flex: 1,
  },
  actionCardContent: {
    padding: 16,
  },
  actionRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  actionInfo: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
  },
  actionTextContainer: {
    marginLeft: 12,
    flex: 1,
  },
  actionButton: {
    marginLeft: 16,
    minWidth: 80,
  },
  actionButtonLabel: {
    fontSize: 14,
  },
  statsContainer: {
    marginTop: 32,
  },
  metricsGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 16,
    marginBottom: 24,
  },
  metricsGridMobile: {
    flexDirection: 'column',
    flexWrap: 'nowrap',
    width: '100%',
    minWidth: 0,
    gap: 12,
  },
  metricCard: {
    flex: 1,
    minWidth: 200,
    elevation: 2,
  },
  metricCardHighlighted: {
    borderWidth: 1,
    borderColor: 'rgba(0,0,0,0.08)',
    elevation: 4,
  },
  metricCardMobile: {
    width: '100%',
    minWidth: 0,
    flexBasis: 'auto',
    flexGrow: 0,
    flexShrink: 1,
  },
  metricCardContent: {
    padding: 16,
  },
  capacitySummary: {
    marginTop: 8,
    gap: 10,
  },
  capacityMetricRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 8,
    minWidth: 0,
  },
  capacityMetricContent: {
    flex: 1,
    minWidth: 0,
    gap: 4,
  },
  capacityMetricLabel: {
    fontWeight: '700',
  },
  metricHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 8,
    justifyContent: 'center',
  },
  metricHeaderWithMenu: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 8,
  },
  metricValueContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 8,
  },
  percentageChangeContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  mapCard: {
    marginTop: 16,
    elevation: 2,
    marginBottom: 24,
  },
  mapContainer: {
    height: 400,
    borderRadius: 8,
    overflow: 'hidden',
  },
  map: {
    width: '100%',
    height: '100%',
  },
}); 